// Phase 11.5 + Mobile-fix — Interactive 3D trajectory viewer.
//
// What this fixes vs the prior revision:
//   1. PAD DEGENERATE AXES — head-on / constant-altitude demos have z=const.
//      Previously {min:200,max:200} produced a zero-thickness axis and
//      ``aspectmode: data`` collapsed the cube. Now every axis is padded
//      to at least ``MIN_AXIS_HALF_M`` either side of its centroid.
//   2. ASPECT MODE = CUBE — at any viewport width the scene is rendered as
//      a regular cube so a 1500 m × 0.1 m × 0 m engagement still
//      shows visible Y/Z lines. The previous "data" mode hid the convergence
//      detail behind a 1-pixel slit on mobile.
//   3. NO IN-PLOT LEGEND — Plotly's default legend was eating ~120 px on a
//      375 px mobile viewport. Replaced by the existing micro-legend
//      below and the corner key (desktop only).
//   4. FRAME INTERCEPT button — fits scene to a 60 m bubble centred on the
//      intercept point so the convergence is the dominant feature.
//   5. INTERCEPT CALLOUT — a labelled annotation reads
//      "Intercept · t=Xs · miss=Ym" right at the intercept point.
//   6. MOBILE MIN-HEIGHT — ``min-h-[60vh]`` ensures the canvas can't
//      collapse below half the viewport on any narrow screen.
//   7. AUTO-FIT ON MOUNT — the camera defaults to a tight perspective on
//      the data centroid (Plotly's normalised camera coordinates already
//      look at the scene midpoint, but the cube aspect is required for
//      that to translate into a useful framing).
//
// ENU coordinate convention is unchanged (x=East, y=North, z=Altitude up).
import { Suspense, lazy, useEffect, useMemo, useState } from "react";

import { runs as runsApi } from "@/api/endpoints";
import Skeleton from "@/components/Skeleton";
import { formatApiError } from "@/lib/formatApiError";

// Hex literals (Plotly does not resolve CSS variables).
const COLORS = {
    paper: "#10151c",
    panel: "#10151C",
    grid: "#232c3a",
    zeroline: "#2e3a4d",
    axisTick: "#a7b1c2",
    axisLabel: "#e6ecf3",
    legendText: "#e6ecf3",
    interceptor: "#3da9fc",
    target: "#f5b544",
    intercept: "#3ccb7f",
    bg0: "#0b0f14",
    refX: "#e74c3c",
    refY: "#3ccb7f",
    refZ: "#3da9fc",
    ground: "#a7b1c2",
};

const Plot = lazy(() => import("react-plotly.js"));

const DEFAULT_TOGGLES = {
    interceptors: true,
    targets: true,
    interceptMarkers: true,
    endpointMarkers: true,
};

// Minimum half-extent (meters) for each scene axis. Prevents Plotly from
// trying to draw a 0-thickness slit when the scenario is planar (constant
// altitude / pure-tail-chase / etc).
const MIN_AXIS_HALF_M = 25.0;

// Bubble half-size (meters) used by the "Frame intercept" preset.
const INTERCEPT_BUBBLE_HALF_M = 60.0;

const HOVER_LINE =
    "<b>%{fullData.name}</b><br>" +
    "X(E)=%{x:.0f} m  Y(N)=%{y:.0f} m  Z(alt)=%{z:.1f} m<br>" +
    "t=%{customdata:.2f} s<extra></extra>";

const HOVER_POINT_INTERCEPT =
    "<b>INTERCEPT %{fullData.name}</b><br>" +
    "X(E)=%{x:.0f} m  Y(N)=%{y:.0f} m  Z(alt)=%{z:.1f} m<br>" +
    "t=%{customdata:.2f} s<extra></extra>";

// ---------------------------------------------------------------------------
// Pad an extents block so every axis has at least 2 * MIN_AXIS_HALF_M total
// width. Returns a shallow-cloned ``{x:{min,max}, y:{min,max}, z:{min,max}}``.
function padExtents(ext) {
    const padOne = (axis, isAlt = false) => {
        const c = (axis.min + axis.max) / 2;
        const half = (axis.max - axis.min) / 2;
        const padded = Math.max(half, MIN_AXIS_HALF_M);
        let lo = c - padded;
        let hi = c + padded;
        if (isAlt) lo = Math.min(lo, 0); // keep ground (z=0) visible for altitude axis
        return { min: lo, max: hi };
    };
    return {
        x: padOne(ext.x),
        y: padOne(ext.y),
        z: padOne(ext.z, true),
    };
}

function buildLineTraces(data, toggles, selectedSampleId) {
    if (!data?.samples?.length) return [];
    const traces = [];
    const samples = selectedSampleId === "all"
        ? data.samples
        : data.samples.filter((s) => String(s.sample_id) === String(selectedSampleId));

    for (const s of samples) {
        const tag = `S${s.sample_id}`;
        if (toggles.interceptors && s.interceptor?.x?.length) {
            traces.push({
                type: "scatter3d",
                mode: "lines",
                name: `Interceptor ${tag}`,
                legendgroup: `interceptor-${s.sample_id}`,
                x: s.interceptor.x,
                y: s.interceptor.y,
                z: s.interceptor.z,
                line: { color: COLORS.interceptor, width: 4 },
                opacity: 0.95,
                customdata: s.interceptor.t,
                hovertemplate: HOVER_LINE,
                showlegend: false,
            });
        }
        if (toggles.targets && s.target?.x?.length) {
            traces.push({
                type: "scatter3d",
                mode: "lines",
                name: `Target ${tag}`,
                legendgroup: `target-${s.sample_id}`,
                x: s.target.x,
                y: s.target.y,
                z: s.target.z,
                line: { color: COLORS.target, width: 4 },
                opacity: 0.95,
                customdata: s.target.t,
                hovertemplate: HOVER_LINE,
                showlegend: false,
            });
        }
        if (toggles.endpointMarkers && s.interceptor?.x?.length) {
            const last = s.interceptor.x.length - 1;
            traces.push({
                type: "scatter3d",
                mode: "markers",
                name: `Interceptor end ${tag}`,
                legendgroup: `interceptor-${s.sample_id}`,
                showlegend: false,
                x: [s.interceptor.x[last]],
                y: [s.interceptor.y[last]],
                z: [s.interceptor.z[last]],
                marker: { size: 6, color: COLORS.interceptor, line: { color: COLORS.bg0, width: 1 } },
                opacity: 0.7,
                hoverinfo: "skip",
            });
        }
        if (toggles.endpointMarkers && s.target?.x?.length) {
            const last = s.target.x.length - 1;
            traces.push({
                type: "scatter3d",
                mode: "markers",
                name: `Target end ${tag}`,
                legendgroup: `target-${s.sample_id}`,
                showlegend: false,
                x: [s.target.x[last]],
                y: [s.target.y[last]],
                z: [s.target.z[last]],
                marker: { size: 6, color: COLORS.target, line: { color: COLORS.bg0, width: 1 } },
                opacity: 0.7,
                hoverinfo: "skip",
            });
        }
        if (toggles.interceptMarkers && s.intercept_point) {
            traces.push({
                type: "scatter3d",
                mode: "markers",
                name: `Intercept ${tag}`,
                legendgroup: `intercept-${s.sample_id}`,
                showlegend: false,
                x: [s.intercept_point.x],
                y: [s.intercept_point.y],
                z: [s.intercept_point.z],
                marker: {
                    size: 12,
                    color: COLORS.intercept,
                    symbol: "circle",
                    line: { color: COLORS.bg0, width: 2 },
                    opacity: 0.95,
                },
                customdata: [s.intercept_point.t],
                hovertemplate: HOVER_POINT_INTERCEPT,
            });
        }
    }
    return traces;
}

function buildSceneScaffolding(extents) {
    const xs = [extents.x.min, extents.x.max];
    const ys = [extents.y.min, extents.y.max];
    const zs = [Math.min(0, extents.z.min), Math.max(extents.z.max, 1)];

    // Ground plane (z=0) — translucent grey square covering the data extents.
    const ground = {
        type: "mesh3d",
        x: [xs[0], xs[1], xs[1], xs[0]],
        y: [ys[0], ys[0], ys[1], ys[1]],
        z: [0, 0, 0, 0],
        i: [0, 0],
        j: [1, 2],
        k: [2, 3],
        color: COLORS.ground,
        opacity: 0.08,
        flatshading: true,
        hoverinfo: "skip",
        showscale: false,
        showlegend: false,
        name: "Ground (Z=0)",
    };

    // RGB reference axes from origin sized at ~10% of the largest extent.
    const refLen =
        0.1 *
        Math.max(
            Math.abs(xs[1] - xs[0]),
            Math.abs(ys[1] - ys[0]),
            Math.abs(zs[1] - zs[0]),
            10,
        );
    const refTrace = (axis, color, end) => ({
        type: "scatter3d",
        mode: "lines",
        x: [0, end[0]],
        y: [0, end[1]],
        z: [0, end[2]],
        line: { color, width: 6 },
        showlegend: false,
        hoverinfo: "skip",
        name: `ref-${axis}`,
    });
    const refAxes = [
        refTrace("X", COLORS.refX, [refLen, 0, 0]),
        refTrace("Y", COLORS.refY, [0, refLen, 0]),
        refTrace("Z", COLORS.refZ, [0, 0, refLen]),
    ];

    return { ground, refAxes, paddedZs: zs };
}

const CAMERA_PERSPECTIVE = {
    eye: { x: 1.4, y: -1.4, z: 0.9 },
    center: { x: 0, y: 0, z: 0 },
    up: { x: 0, y: 0, z: 1 },
};
const CAMERA_TOPDOWN = {
    eye: { x: 0, y: 0, z: 2.4 },
    center: { x: 0, y: 0, z: 0 },
    up: { x: 0, y: 1, z: 0 },
};
// Slightly closer eye for the intercept-bubble framing (camera lives in
// the scene's normalised cube, so a tighter axis range gives a tighter
// view automatically — the eye just needs to be inside the cube).
const CAMERA_INTERCEPT = {
    eye: { x: 1.1, y: -1.1, z: 0.8 },
    center: { x: 0, y: 0, z: 0 },
    up: { x: 0, y: 0, z: 1 },
};

function axisCfg(title) {
    return {
        title: { text: title, font: { color: COLORS.axisLabel, size: 12, family: "Inter, sans-serif" } },
        tickfont: { color: COLORS.axisTick, size: 10 },
        backgroundcolor: COLORS.panel,
        gridcolor: COLORS.grid,
        zerolinecolor: COLORS.zeroline,
        showbackground: true,
        color: COLORS.axisTick,
    };
}

// Pick a representative intercept point from the data for the
// "Frame intercept" preset + scene annotation. Falls back to None.
function pickInterceptForFraming(data, selectedSampleId) {
    if (!data?.samples?.length) return null;
    const candidates =
        selectedSampleId === "all"
            ? data.samples
            : data.samples.filter((s) => String(s.sample_id) === String(selectedSampleId));
    for (const s of candidates) {
        if (s.intercept_point && Number.isFinite(s.intercept_point.x)) return s;
    }
    return null;
}

// `fetcher` lets a non-authed caller (the public /demo gallery) reuse this
// component against /api/public/demos/{id}/trajectory-data instead of the
// authed /api/runs/{id}/trajectory-data. Both endpoints return the same
// shape (run_service.read_trajectory_data), so the rest of the component
// stays untouched. Default keeps the operator console behaviour intact.
export default function Trajectory3D({
    runId,
    status,
    hasTrajectoryArtifact,
    fetcher = (id, opts) => runsApi.trajectoryData(id, opts),
}) {
    const enabled = status === "succeeded" && hasTrajectoryArtifact;

    const [data, setData] = useState(null);
    const [error, setError] = useState(null);
    const [errorStatus, setErrorStatus] = useState(0);
    const [loading, setLoading] = useState(false);
    const [selectedSampleId, setSelectedSampleId] = useState("all");
    const [toggles, setToggles] = useState(DEFAULT_TOGGLES);
    const [revision, setRevision] = useState(0);
    const [camera, setCamera] = useState(CAMERA_PERSPECTIVE);
    // null  → fit padded full extents (default).
    // "intercept" → fit a tight bubble centred on the intercept point.
    const [framingMode, setFramingMode] = useState(null);

    // Mobile-fix follow-up — JS-driven viewport-width gate for the mobile
    // intercept badge. We previously relied on Tailwind's ``sm:hidden`` alone
    // (i.e. "hide on >=640 px") and let the implicit ``display: block``
    // browser default make it visible below 640 px. On real iOS Safari this
    // resolved to ``display: none`` for some sub-meter-miss runs (the badge
    // ended up in the DOM with 0×0 bounding rect). The state below
    // unconditionally re-renders the badge on real-pixel-width changes, so
    // the existence of the JSX subtree itself is the gate — no CSS
    // specificity guesswork.
    const [isMobile, setIsMobile] = useState(() => {
        if (typeof window === "undefined") return false;
        return window.matchMedia("(max-width: 639px)").matches;
    });
    useEffect(() => {
        if (typeof window === "undefined") return;
        const mql = window.matchMedia("(max-width: 639px)");
        const update = (e) => setIsMobile(e.matches);
        // matchMedia listener API
        if (mql.addEventListener) mql.addEventListener("change", update);
        else mql.addListener(update);
        // also handle plain resize (some embedded webviews don't fire mql change)
        const onResize = () => setIsMobile(window.innerWidth < 640);
        window.addEventListener("resize", onResize);
        // Sync once on mount in case viewport changed between SSR/CSR.
        setIsMobile(window.innerWidth < 640);
        return () => {
            if (mql.removeEventListener) mql.removeEventListener("change", update);
            else mql.removeListener(update);
            window.removeEventListener("resize", onResize);
        };
    }, []);

    useEffect(() => {
        if (!enabled || !runId) return;
        let cancelled = false;
        setLoading(true);
        setError(null);
        setErrorStatus(0);
        fetcher(runId, { max_samples: 12 })
            .then((d) => {
                if (cancelled) return;
                setData(d);
                setSelectedSampleId("all");
                setFramingMode(null);
                setCamera(CAMERA_PERSPECTIVE);
            })
            .catch((e) => {
                if (cancelled) return;
                const code = e?.response?.status || 0;
                const detail = formatApiError(e, "Failed to load trajectory data.");
                setErrorStatus(code);
                setError(typeof detail === "string" ? detail : JSON.stringify(detail));
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });
        return () => {
            cancelled = true;
        };
        // Phase 14.8 W5 — `fetcher` is a prop; adding it to deps would
        // refire the heavy GET on every parent render unless every
        // caller wraps its `fetcher` prop in useCallback. Effect
        // intentionally fires on operator-input changes only (runId /
        // enabled).
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [runId, enabled]);

    // Padded extents are the source of truth for both the scene scaffold
    // and the layout's per-axis range.
    const paddedExtents = useMemo(
        () => (data ? padExtents(data.extents) : null),
        [data],
    );
    const scaffold = useMemo(
        () => (paddedExtents ? buildSceneScaffolding(paddedExtents) : null),
        [paddedExtents],
    );
    const interceptSample = useMemo(
        () => pickInterceptForFraming(data, selectedSampleId),
        [data, selectedSampleId],
    );

    const traces = useMemo(() => {
        if (!data || !scaffold) return [];
        const lineTraces = buildLineTraces(data, toggles, selectedSampleId);
        return [scaffold.ground, ...scaffold.refAxes, ...lineTraces];
    }, [data, scaffold, toggles, selectedSampleId]);

    const layout = useMemo(() => {
        if (!data || !paddedExtents || !scaffold) return null;

        // Choose axis ranges: either the padded full extents (default) or
        // a tight bubble around the intercept point ("Frame intercept").
        let xr = [paddedExtents.x.min, paddedExtents.x.max];
        let yr = [paddedExtents.y.min, paddedExtents.y.max];
        let zr = scaffold.paddedZs;
        if (framingMode === "intercept" && interceptSample) {
            const ip = interceptSample.intercept_point;
            const half = INTERCEPT_BUBBLE_HALF_M;
            xr = [ip.x - half, ip.x + half];
            yr = [ip.y - half, ip.y + half];
            zr = [Math.max(0, ip.z - half), ip.z + half];
        }

        // Annotation that labels the intercept point with miss + t.
        // Always rendered when an intercept is available so the user
        // never has to hunt for it. Position is the intercept point;
        // text floats up + east a touch so it doesn't sit on the marker.
        const annotations = [];
        if (interceptSample && toggles.interceptMarkers) {
            const ip = interceptSample.intercept_point;
            const miss = interceptSample.miss_distance_m;
            const t = ip?.t;
            const missTxt =
                Number.isFinite(miss)
                    ? miss < 1
                        ? `miss ${miss.toFixed(2)} m`
                        : `miss ${miss.toFixed(1)} m`
                    : "";
            const tTxt = Number.isFinite(t) ? `t=${t.toFixed(1)} s` : "";
            const label = ["Intercept", tTxt, missTxt].filter(Boolean).join(" · ");
            annotations.push({
                showarrow: true,
                arrowhead: 0,
                arrowwidth: 1,
                arrowcolor: COLORS.intercept,
                x: ip.x,
                y: ip.y,
                z: ip.z,
                text: label,
                font: {
                    family: "ui-monospace, SF Mono, monospace",
                    size: 11,
                    color: COLORS.intercept,
                },
                bgcolor: "rgba(11,15,20,0.85)",
                bordercolor: COLORS.intercept,
                borderwidth: 1,
                borderpad: 4,
                xanchor: "left",
                yanchor: "bottom",
                ax: 18,
                ay: -22,
                opacity: 0.96,
            });
        }

        return {
            paper_bgcolor: COLORS.paper,
            plot_bgcolor: COLORS.paper,
            scene: {
                xaxis: { ...axisCfg("X · East (m)"), range: xr },
                yaxis: { ...axisCfg("Y · North (m)"), range: yr },
                zaxis: { ...axisCfg("Z · Altitude (m)"), range: zr },
                // Cube aspect mode keeps the scene visible when one axis
                // is degenerate (e.g. constant-altitude head-on demo). The
                // axes themselves still report real meters so the labels
                // stay correct.
                aspectmode: "cube",
                camera,
                dragmode: "orbit",
                annotations,
            },
            // No in-plot legend — the colour key in the corner (desktop)
            // and the micro-legend below (mobile + desktop) replace it.
            // Plotly's default in-plot legend was eating ~120 px of canvas
            // width on mobile, leaving the trajectory invisible.
            showlegend: false,
            margin: { l: 0, r: 0, t: 0, b: 0 },
            uirevision: "trajectory-3d",
        };
    }, [data, paddedExtents, scaffold, camera, framingMode, interceptSample, toggles.interceptMarkers]);

    if (!enabled) return null;

    if (loading) {
        return (
            <div data-testid="trajectory-3d-loading" className="space-y-3">
                <Skeleton className="h-9 w-full" />
                <Skeleton className="min-h-[60vh] sm:min-h-[480px] lg:min-h-[540px] w-full" />
            </div>
        );
    }

    if (errorStatus === 422) {
        return (
            <div
                className="rounded-md border border-[var(--ux-border-1)] bg-[var(--ux-bg-1)] p-6 text-center"
                data-testid="trajectory-3d-no-data"
            >
                <div className="text-[14px] font-semibold text-[var(--ux-text-1)]">No trajectory data</div>
                <p className="mt-1 text-[13px] text-[var(--ux-text-3)] max-w-md mx-auto">
                    {error || "This run has no trajectory data (output preset has store_trajectories disabled)."}
                </p>
            </div>
        );
    }
    if (error) {
        return (
            <div
                className="rounded-md border border-[var(--ux-danger)]/40 bg-[var(--ux-danger-soft)] p-4 text-[13px] text-[var(--ux-danger)]"
                data-testid="trajectory-3d-error"
            >
                Could not load trajectory: {error}
            </div>
        );
    }
    if (!data) return null;

    const sampleOptions = [
        { value: "all", label: `All samples (${data.sample_count_returned})` },
        ...data.samples.map((s) => ({
            value: String(s.sample_id),
            label: `Sample ${s.sample_id}${s.outcome ? ` · ${s.outcome}` : ""}`,
        })),
    ];

    const setCameraAndBump = (cam, mode = null) => {
        setCamera(cam);
        setFramingMode(mode);
        setRevision((r) => r + 1);
    };

    const interceptAvailable = !!interceptSample;
    const isInterceptFramed = framingMode === "intercept";

    return (
        <div data-testid="trajectory-3d" className="space-y-3">
            {/* TOOLBAR — stacks on mobile */}
            <div className="flex flex-col sm:flex-row flex-wrap items-stretch sm:items-center gap-2 sm:gap-3 bg-[var(--ux-bg-1)] border border-[var(--ux-border-1)] rounded-md px-3 py-2">
                <label className="inline-flex items-center gap-2 text-[12px] text-[var(--ux-text-2)]">
                    Sample
                    <select
                        data-testid="trajectory-3d-sample-select"
                        value={selectedSampleId}
                        onChange={(e) => setSelectedSampleId(e.target.value)}
                        className="bg-[var(--ux-bg-2)] text-[var(--ux-text-1)] text-[12px] border border-[var(--ux-border-1)] rounded px-2 h-8 min-h-[32px] focus:outline-none focus:border-[var(--ux-accent)]"
                    >
                        {sampleOptions.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                        ))}
                    </select>
                </label>
                <div className="flex flex-wrap items-center gap-3">
                    {[
                        { key: "interceptors", label: "Interceptors" },
                        { key: "targets", label: "Targets" },
                        { key: "interceptMarkers", label: "Intercept" },
                        { key: "endpointMarkers", label: "Endpoints" },
                    ].map((t) => (
                        <label
                            key={t.key}
                            className="inline-flex items-center gap-1.5 text-[12px] text-[var(--ux-text-2)] select-none cursor-pointer"
                        >
                            <input
                                type="checkbox"
                                checked={!!toggles[t.key]}
                                onChange={(e) => setToggles((s) => ({ ...s, [t.key]: e.target.checked }))}
                                data-testid={`trajectory-3d-toggle-${t.key}`}
                                className="accent-[var(--ux-accent)]"
                            />
                            {t.label}
                        </label>
                    ))}
                </div>
                <div className="flex flex-wrap items-center gap-2 sm:ml-auto">
                    <button
                        type="button"
                        onClick={() =>
                            interceptAvailable
                                ? setCameraAndBump(CAMERA_INTERCEPT, "intercept")
                                : null
                        }
                        disabled={!interceptAvailable}
                        data-testid="trajectory-3d-frame-intercept"
                        className={[
                            "text-[12px] px-3 h-8 min-h-[32px] rounded border transition-colors",
                            isInterceptFramed
                                ? "border-[var(--ux-success)] text-[var(--ux-success)] bg-[var(--ux-success-soft)]"
                                : "border-[var(--ux-border-1)] bg-[var(--ux-bg-2)] text-[var(--ux-text-1)] hover:border-[var(--ux-accent)]",
                            !interceptAvailable && "opacity-40 cursor-not-allowed",
                        ].filter(Boolean).join(" ")}
                        title={
                            interceptAvailable
                                ? "Zoom camera to a 60 m bubble around the intercept point"
                                : "No intercept point available for this run"
                        }
                    >
                        Frame intercept
                    </button>
                    <button
                        type="button"
                        onClick={() => setCameraAndBump(CAMERA_TOPDOWN, null)}
                        data-testid="trajectory-3d-topdown"
                        className="text-[12px] px-3 h-8 min-h-[32px] rounded border border-[var(--ux-border-1)] bg-[var(--ux-bg-2)] text-[var(--ux-text-1)] hover:border-[var(--ux-accent)] transition-colors"
                        title="Top-down view (XY plane only)"
                    >
                        Top-down (XY)
                    </button>
                    <button
                        type="button"
                        onClick={() => setCameraAndBump(CAMERA_PERSPECTIVE, null)}
                        data-testid="trajectory-3d-reset"
                        className="text-[12px] px-3 h-8 min-h-[32px] rounded border border-[var(--ux-border-1)] bg-[var(--ux-bg-2)] text-[var(--ux-text-1)] hover:border-[var(--ux-accent)] transition-colors"
                    >
                        Reset view
                    </button>
                </div>
                <span className="text-[11px] font-mono text-[var(--ux-text-3)] sm:basis-full sm:text-right">
                    {data.sample_count_returned}/{data.sample_count_total} samples · decimate ×{data.decimation_factor} · {data.coordinate_frame || "ENU"}
                </span>
            </div>

            {/* PLOT WITH OVERLAY LEGEND */}
            <div
                className="relative"
                data-testid="trajectory-3d-plot-container"
            >
                <Suspense
                    fallback={
                        <Skeleton className="min-h-[60vh] sm:min-h-[480px] lg:min-h-[540px] w-full" />
                    }
                >
                    <Plot
                        data={traces}
                        layout={layout}
                        revision={revision}
                        config={{
                            displaylogo: false,
                            responsive: true,
                            modeBarButtonsToRemove: ["lasso2d", "select2d"],
                        }}
                        useResizeHandler
                        style={{ width: "100%", height: "100%" }}
                        className="min-h-[60vh] sm:min-h-[480px] lg:min-h-[540px] w-full"
                        onRelayout={(ev) => {
                            if (ev?.["scene.camera"]) setCamera(ev["scene.camera"]);
                        }}
                        data-testid="trajectory-3d-plot"
                    />
                </Suspense>

                {/* Top-right colour-key overlay (desktop only — too cramped on mobile) */}
                <div
                    className="hidden sm:block absolute top-3 right-3 px-3 py-2 rounded-md border border-[var(--ux-border-1)] bg-[var(--ux-bg-2)]/90 backdrop-blur-sm font-mono text-[11px] leading-tight text-[var(--ux-text-2)] pointer-events-none"
                    data-testid="trajectory-3d-key"
                >
                    <div className="flex items-center gap-2"><span className="inline-block w-3 h-[2px]" style={{ background: COLORS.interceptor }} /> Interceptor</div>
                    <div className="flex items-center gap-2"><span className="inline-block w-3 h-[2px]" style={{ background: COLORS.target }} /> Target</div>
                    <div className="flex items-center gap-2"><span className="inline-block w-2 h-2 rounded-full" style={{ background: COLORS.intercept }} /> Intercept</div>
                    <div className="mt-1 pt-1 border-t border-[var(--ux-border-1)] flex items-center gap-2">
                        <span className="inline-block w-3 h-[2px]" style={{ background: COLORS.refX }} /> X
                        <span className="inline-block w-3 h-[2px] ml-1" style={{ background: COLORS.refY }} /> Y
                        <span className="inline-block w-3 h-[2px] ml-1" style={{ background: COLORS.refZ }} /> Z
                    </div>
                    <div className="flex items-center gap-2"><span className="inline-block w-3 h-[6px] opacity-30" style={{ background: COLORS.ground }} /> Ground (Z=0)</div>
                </div>

                {/* MOBILE intercept stat badge — bottom-left of canvas. Always
                    rendered when an intercept exists AND the JS-driven mobile
                    state is active. Uses ``block`` explicitly + only renders
                    on mobile, so neither inherited ``display: flex/grid`` from
                    the wrapper nor any Tailwind ``sm:`` specificity oddity on
                    real Safari can hide it. The miss is rounded to 2 decimal
                    places consistently so a 0.03 m miss never renders as "0 m". */}
                {interceptAvailable && isMobile && (
                    <div
                        className="block absolute bottom-3 left-3 px-2.5 py-1.5 rounded-md border border-[var(--ux-success)]/40 bg-[rgba(11,15,20,0.92)] backdrop-blur-sm font-mono text-[10px] leading-tight text-[var(--ux-success)] pointer-events-none z-20"
                        style={{ display: "block" }}
                        data-testid="trajectory-3d-mobile-intercept-badge"
                    >
                        <div className="text-[var(--ux-text-1)] font-semibold tracking-wide">INTERCEPT</div>
                        {Number.isFinite(interceptSample.miss_distance_m) && (
                            <div data-testid="trajectory-3d-mobile-intercept-badge-miss">
                                miss = {interceptSample.miss_distance_m.toFixed(2)} m
                            </div>
                        )}
                        {Number.isFinite(interceptSample.intercept_point?.t) && (
                            <div>t = {interceptSample.intercept_point.t.toFixed(1)} s</div>
                        )}
                    </div>
                )}
            </div>

            {/* MICRO-LEGEND BELOW (visible on every viewport) */}
            <div className="flex flex-wrap gap-3 text-[11px] text-[var(--ux-text-3)] px-1">
                <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-[2px]" style={{ background: COLORS.interceptor }} /> Interceptor
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="w-3 h-[2px]" style={{ background: COLORS.target }} /> Target
                </span>
                <span className="inline-flex items-center gap-1.5">
                    <span className="w-2 h-2 rounded-full" style={{ background: COLORS.intercept }} /> Intercept point
                </span>
                <span className="ml-auto font-mono hidden sm:inline">
                    drag to rotate · scroll to zoom · double-click to reset
                </span>
                <span className="basis-full sm:hidden font-mono">
                    pinch to zoom · drag to rotate · tap "Frame intercept" for the convergence detail
                </span>
            </div>
        </div>
    );
}
